/**
 * @module features/legal/PrivacyPolicyPage
 * @description Privacy Policy document for RxDeviceGateway.
 * Governed by Roborosx Omni Tech Solutions LLP.
 */

import * as React from "react";
import { LegalLayout } from "./LegalLayout";
import { APP_NAME, ORG_NAME, ORG_PRIVACY_EMAIL, ORG_SUPPORT_EMAIL } from "@/config/env";

export function PrivacyPolicyPage() {
  React.useEffect(() => {
    document.title = `Privacy Policy — ${APP_NAME} | ${ORG_NAME}`;
    window.scrollTo(0, 0);
  }, []);

  return (
    <LegalLayout
      title="Privacy Policy"
      subtitle={`How ${ORG_NAME} collects, safeguards, and processes operational telemetry and personal credentials across the ${APP_NAME} platform.`}
      lastUpdated="October 24, 2026"
      badge="Privacy Policy"
    >
      <section className="space-y-3">
        <h2 className="text-lg sm:text-xl font-bold tracking-tight text-foreground">
          1. Introduction & Overview
        </h2>
        <p className="text-muted-foreground leading-relaxed">
          <strong className="text-foreground">{ORG_NAME}</strong> (&ldquo;we&rdquo;, &ldquo;our&rdquo;, or &ldquo;us&rdquo;) is committed to maintaining robust privacy, security, and cryptographic confidentiality protections for users and enterprise devices operating on the <strong className="text-foreground">{APP_NAME}</strong> platform.
        </p>
        <p className="text-muted-foreground leading-relaxed">
          This Privacy Policy details our practices concerning the collection, storage, utilization, and disclosure of personal credentials, telemetry records, and hardware metadata gathered through the {APP_NAME} administrative consoles, client portals, and IoT gateway APIs.
        </p>
      </section>

      <section className="space-y-3">
        <h2 className="text-lg sm:text-xl font-bold tracking-tight text-foreground">
          2. Information We Collect
        </h2>
        <p className="text-muted-foreground leading-relaxed">
          Depending on your role and deployment context within {APP_NAME}, we process the following categories of data:
        </p>

        <div className="space-y-4">
          <div className="rounded-xl border border-border/80 bg-card p-4 space-y-1.5">
            <h3 className="font-semibold text-sm text-foreground">
              A. User Account & Authentication Credentials
            </h3>
            <p className="text-xs text-muted-foreground leading-relaxed">
              When an administrator provisions an account or signs in: full name, corporate email address, contact telephone number, and role classification (Super Admin, Client, Operator).
            </p>
            <p className="text-xs text-muted-foreground leading-relaxed">
              <strong className="text-foreground">Authentication Secrets:</strong> Passwords hashed using Argon2id with memory-hard cost parameters; FIDO2/WebAuthn public credential keys and attestation signatures; TOTP secret keys and cryptographically hashed recovery codes. Plaintext passwords are never accessible, logged, or stored.
            </p>
          </div>

          <div className="rounded-xl border border-border/80 bg-card p-4 space-y-1.5">
            <h3 className="font-semibold text-sm text-foreground">
              B. Hardware & Edge Device Telemetry
            </h3>
            <p className="text-xs text-muted-foreground leading-relaxed">
              To uniquely identify and securely route edge communications across the gateway fleet:
            </p>
            <ul className="list-disc pl-5 text-xs text-muted-foreground space-y-1">
              <li>Hardware IEEE 802 Media Access Control (MAC) addresses.</li>
              <li>Serial and Registered Device Identifiers.</li>
              <li>Concrete subtype attributes (for display modules: OS name, semver OS version, and SHA-256 build verification hashes).</li>
              <li>Operational state flags (Provisioned, Active, Maintenance, Offline, Decommissioned).</li>
              <li>Device last-seen heartbeat timestamps and edge gateway connectivity metrics.</li>
            </ul>
          </div>

          <div className="rounded-xl border border-border/80 bg-card p-4 space-y-1.5">
            <h3 className="font-semibold text-sm text-foreground">
              C. Audit Trail & Diagnostic Logs
            </h3>
            <p className="text-xs text-muted-foreground leading-relaxed">
              In accordance with enterprise compliance mandates, the platform maintains immutable audit logs via <code className="font-mono text-[11px] bg-muted px-1.5 py-0.5 rounded">django-auditlog</code> capturing identity transitions, device state changes, firmware upgrade histories, mutating actor identities, originating IP addresses, and correlated <code className="font-mono text-[11px] bg-muted px-1.5 py-0.5 rounded">request_id</code> traces.
            </p>
          </div>
        </div>
      </section>

      <section className="space-y-3">
        <h2 className="text-lg sm:text-xl font-bold tracking-tight text-foreground">
          3. How We Use Collected Data
        </h2>
        <p className="text-muted-foreground leading-relaxed">
          {ORG_NAME} processes collected data solely for legitimate business, operational, and security purposes, including:
        </p>
        <ul className="list-disc pl-5 space-y-2 text-muted-foreground">
          <li>
            <strong className="text-foreground">Fleet Orchestration:</strong> Validating device authenticity, enforcing hardware uniqueness across the central registry, and routing edge MQTT messages.
          </li>
          <li>
            <strong className="text-foreground">Firmware Integrity:</strong> Logging and certifying over-the-air (OTA) updates, tracking version history timelines, and detecting unauthorized firmware alterations via cryptographic build hashes.
          </li>
          <li>
            <strong className="text-foreground">Access Security:</strong> Enforcing multi-factor authentication, verifying WebAuthn passkey signatures, and blocking brute-force or replay attacks.
          </li>
          <li>
            <strong className="text-foreground">Compliance & Forensics:</strong> Providing auditable accountability logs for enterprise regulatory compliance and security incident investigation.
          </li>
        </ul>
      </section>

      <section className="space-y-3">
        <h2 className="text-lg sm:text-xl font-bold tracking-tight text-foreground">
          4. Security & Cryptographic Protections
        </h2>
        <p className="text-muted-foreground leading-relaxed">
          Security is engineered into the foundation of {APP_NAME}. We enforce defense-in-depth measures:
        </p>
        <ul className="list-disc pl-5 space-y-2 text-muted-foreground">
          <li>
            <strong className="text-foreground">Encryption in Transit:</strong> All communication between browsers, the React client, central REST APIs, and edge MQTT brokers is strictly encrypted via TLS 1.3 / HTTPS / mTLS.
          </li>
          <li>
            <strong className="text-foreground">Encryption at Rest:</strong> Relational databases and Redis broker state employ AES-256 volume encryption.
          </li>
          <li>
            <strong className="text-foreground">Credential Hardening:</strong> Passwords are protected with Argon2id; passkeys leverage public-key cryptography where private keys never leave the hardware enclave.
          </li>
          <li>
            <strong className="text-foreground">Network Isolation:</strong> Database engines, Redis cache servers, and background workers are isolated within private virtual networks and never exposed to the public internet.
          </li>
        </ul>
      </section>

      <section className="space-y-3">
        <h2 className="text-lg sm:text-xl font-bold tracking-tight text-foreground">
          5. Third-Party Integrations & Sub-processors
        </h2>
        <p className="text-muted-foreground leading-relaxed">
          We do not sell, rent, or monetize your personal or device data. Limited data exchange occurs only with vetted service providers necessary for platform operation:
        </p>
        <ul className="list-disc pl-5 space-y-2 text-muted-foreground">
          <li>
            <strong className="text-foreground">Google Identity Services:</strong> When you select &ldquo;Sign in with Google&rdquo;, authentication tokens are exchanged securely with Google APIs to verify account ownership.
          </li>
          <li>
            <strong className="text-foreground">Cloud Infrastructure & Telemetry:</strong> Enterprise cloud hosting partners providing secured compute and storage under strict Business Associate and Data Protection Agreements.
          </li>
        </ul>
      </section>

      <section className="space-y-3">
        <h2 className="text-lg sm:text-xl font-bold tracking-tight text-foreground">
          6. Data Retention & Hardware Decommissioning
        </h2>
        <p className="text-muted-foreground leading-relaxed">
          User account records remain active until an authorized Super Administrator executes an account removal.
        </p>
        <p className="text-muted-foreground leading-relaxed">
          When physical hardware is decommissioned in {APP_NAME}, the record is soft-deleted (<code className="font-mono text-[11px] bg-muted px-1.5 py-0.5 rounded">deleted_at</code> timestamped and hidden from active operational views). The hardware MAC address and Registered Device ID remain permanently locked in the database to prevent hardware spoofing, impersonation, or historical audit corruption. Decommissioned units may be restored by Super Administrators via the Reactivation workflow.
        </p>
      </section>

      <section className="space-y-3">
        <h2 className="text-lg sm:text-xl font-bold tracking-tight text-foreground">
          7. Your Rights & Data Subject Requests
        </h2>
        <p className="text-muted-foreground leading-relaxed">
          Subject to applicable privacy regulations (such as GDPR, DPDP Act, and state privacy statutes), you have the right to request access to, correction of, or export of your personal credentials. Because device telemetry forms an integral part of hardware auditability and enterprise security, requests affecting operational devices must be routed through your organization&apos;s appointed Super Administrator.
        </p>
      </section>

      <section className="space-y-3">
        <h2 className="text-lg sm:text-xl font-bold tracking-tight text-foreground">
          8. Contact Our Data Protection Team
        </h2>
        <p className="text-muted-foreground leading-relaxed">
          If you have questions, feedback, or concerns regarding this Privacy Policy or our security practices, please contact:
        </p>
        <div className="rounded-xl border border-border/80 bg-card p-4 space-y-1 text-sm font-sans text-foreground">
          <p className="font-semibold">{ORG_NAME}</p>
          <p className="text-muted-foreground text-xs">Attn: Data Protection Officer / Privacy Officer</p>
          <p className="text-xs">
            Privacy Email:{" "}
            <a href={`mailto:${ORG_PRIVACY_EMAIL}`} className="underline text-foreground">
              {ORG_PRIVACY_EMAIL}
            </a>
          </p>
          <p className="text-xs">
            General Inquiries:{" "}
            <a href={`mailto:${ORG_SUPPORT_EMAIL}`} className="underline text-foreground">
              {ORG_SUPPORT_EMAIL}
            </a>
          </p>
        </div>
      </section>
    </LegalLayout>
  );
}
